import { type DealFormData, dealFormField, pmpDealType } from '@openx/types/deal.ts';
import { BUDGET_MODE, BUDGET_TYPE, type MonetizingApi, PACING_MODE } from '@openx/types/monetizing.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router';
import { useDealContext } from '../../../../../utils/context/DealContext.tsx';
import type { LocationState } from '../../../../DealForm.types.ts';
import { areMonetizingDifferent } from './areMonetizingDifferent.ts';

const revalidate = {
  shouldDirty: true,
  shouldValidate: true,
};

type UsePacingEffectData = {
  observedDealType: DealFormData['pmp_deal_type'] | null;
  observedIsPacingEnabled: boolean | null;
  observedMonetizingMode: DealFormData['pacing_mode'] | null;
  observedBudgetType: DealFormData['budget_type'] | null;
  observedBudgetMode: DealFormData['budget_mode'] | null;
  observedMonetizingUid: DealFormData['monetizing_uid'] | null;
  isLoading: boolean;
  monetizing: MonetizingApi | undefined;
};

export const usePacingEffect = () => {
  const {
    setValue,
    trigger,
    getValues,
    formState: { touchedFields, isSubmitted, dirtyFields },
  } = useFormContext<DealFormData>();
  const { isPub } = usePermissionContext();

  const locationState: LocationState = useLocation().state;
  const isAsTemplate = locationState?.useSharedTargeting || locationState?.useAsTemplate;
  const observableMonetizingUid = useWatch({ name: dealFormField.MONETIZING_UID });
  const { useMonetizingFetch, isOa } = useDealContext();
  const { isLoading, monetizing } = useMonetizingFetch(observableMonetizingUid, isAsTemplate);
  const observableDealType = useWatch({ name: dealFormField.PMP_DEAL_TYPE });
  const observableIsPacingEnabled = useWatch({ name: dealFormField.IS_PACING_ENABLED });
  const observableMonetizingMode = useWatch({ name: dealFormField.PACING_MODE });
  const observableBudgetType = useWatch({ name: dealFormField.BUDGET_TYPE });
  const observableBudgetMode = useWatch({ name: dealFormField.BUDGET_MODE });

  const previousData = useRef<UsePacingEffectData>({
    observedDealType: observableDealType,
    observedIsPacingEnabled: observableIsPacingEnabled,
    observedMonetizingMode: observableMonetizingMode,
    observedBudgetType: observableBudgetType,
    observedBudgetMode: observableBudgetMode,
    observedMonetizingUid: observableMonetizingUid,
    isLoading,
    monetizing,
  });

  const templateApplied = useRef(false);
  const isPacingOptionalForDealType =
    observableDealType === pmpDealType.PreferredDeal ||
    (isOa && observableDealType === pmpDealType.WithinOpenAuction) ||
    (isPub && observableDealType === pmpDealType.PrivateAuction);

  const disablePacing = useCallback(() => {
    setValue(dealFormField.IS_PACING_ENABLED, false, revalidate);
    setValue(dealFormField.MONETIZING_BUDGET, '', revalidate);
    setValue(dealFormField.PACING_MODE, null, revalidate);
    setValue(dealFormField.MONETIZING_REGION, null, revalidate);
    setValue(dealFormField.BUDGET_TYPE, null, revalidate);
    setValue(dealFormField.BUDGET_MODE, null, revalidate);
    setValue(dealFormField.MONETIZING_CADENCE_UNIT, null, revalidate);
  }, [setValue]);

  const data = useMemo<UsePacingEffectData>(() => {
    const actual = {
      observedDealType: observableDealType,
      observedIsPacingEnabled: observableIsPacingEnabled,
      observedMonetizingMode: observableMonetizingMode,
      observedBudgetType: observableBudgetType,
      observedBudgetMode: observableBudgetMode,
      observedMonetizingUid: observableMonetizingUid,
      isLoading,
      monetizing,
    };
    const previous = previousData.current;

    if (
      previous.observedDealType === actual.observedDealType &&
      previous.observedIsPacingEnabled === actual.observedIsPacingEnabled &&
      previous.observedMonetizingMode === actual.observedMonetizingMode &&
      previous.observedBudgetType === actual.observedBudgetType &&
      previous.observedBudgetMode === actual.observedBudgetMode &&
      previous.observedMonetizingUid === actual.observedMonetizingUid &&
      previous.isLoading === actual.isLoading &&
      !areMonetizingDifferent(previous.monetizing, actual.monetizing)
    ) {
      return previous;
    }

    previousData.current = actual;

    return actual;
  }, [
    observableDealType,
    observableIsPacingEnabled,
    observableMonetizingMode,
    observableBudgetType,
    observableBudgetMode,
    observableMonetizingUid,
    isLoading,
    monetizing,
  ]);

  useEffect(() => {
    // Set default values for pacing fields when pacing is enabled
    if (data.observedIsPacingEnabled && !data.observedMonetizingMode) {
      setValue(dealFormField.PACING_MODE, PACING_MODE.EVENLY, revalidate);
    }
  }, [data.observedIsPacingEnabled, data.observedMonetizingMode, setValue]);

  useEffect(() => {
    // Set default values for pacing fields when pacing is enabled
    if (data.observedIsPacingEnabled && !data.observedBudgetType) {
      setValue(dealFormField.BUDGET_TYPE, BUDGET_TYPE.SPEND, revalidate);
    }
  }, [data.observedIsPacingEnabled, data.observedBudgetType, setValue]);

  useEffect(() => {
    // Set default values for pacing fields when pacing is enabled
    if (data.observedIsPacingEnabled && !data.observedBudgetMode) {
      setValue(dealFormField.BUDGET_MODE, BUDGET_MODE.CAMPAIGN_FLIGHT_TIME, revalidate);
    }
  }, [data.observedIsPacingEnabled, data.observedBudgetMode, setValue]);

  useEffect(() => {
    if (!data.observedIsPacingEnabled && !data.monetizing) {
      // Clear all pacing fields when pacing is disabled
      disablePacing();
    }

    trigger(dealFormField.START_DATE);
    // Only revalidate end date when user has already interacted with (or after a submit attempt,
    // or when monetizing data was just loaded e.g. via "Use as Template"), so toggling pacing
    // doesn't surface "required" errors on untouched end_date field.
    if (touchedFields.end_date || isSubmitted || data.monetizing) {
      trigger(dealFormField.END_DATE);
    }
  }, [data.observedIsPacingEnabled, disablePacing, trigger, data.monetizing, touchedFields.end_date, isSubmitted]);

  useEffect(() => {
    if (data.observedDealType === pmpDealType.Guaranteed) {
      // for pg deal in deal create form pacing must be enabled
      setValue(dealFormField.IS_PACING_ENABLED, true, revalidate);
    } else if (isPacingOptionalForDealType) {
      return;
    } else {
      // deals other than pd and pg should have pacing disabled
      disablePacing();
    }
  }, [data.observedDealType, disablePacing, setValue, isPacingOptionalForDealType]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary deps are included intentionally
  useEffect(() => {
    // When used Use As Template option, we need to apply the monetizing values to the form and validate all form fields
    if (isAsTemplate || data.monetizing) {
      if (!data?.isLoading && data.monetizing && !templateApplied.current) {
        templateApplied.current = true;
        const { pacing_mode, budget, region, budget_type } = data.monetizing;
        const hasCadences = 'cadences' in data.monetizing && !!data.monetizing.cadences?.length;

        setValue(dealFormField.IS_PACING_ENABLED, true, revalidate);
        setValue(dealFormField.PACING_MODE, pacing_mode, revalidate);
        setValue(dealFormField.MONETIZING_REGION, region, revalidate);
        setValue(dealFormField.BUDGET_TYPE, budget_type, revalidate);
        setValue(
          dealFormField.BUDGET_MODE,
          budget ? BUDGET_MODE.CAMPAIGN_FLIGHT_TIME : BUDGET_MODE.CADENCE,
          revalidate
        );

        // Monetizing (budget/cadence) is fetched separately from - and can resolve later than -
        // the main deal load, so the user may already be typing into the budget field by the time
        // this runs. Don't clobber a value the user has already edited with the just-arrived data.
        // dirtyFields alone isn't enough: React Hook Form derives dirtyFields from "current value
        // !== defaultValues" regardless of the shouldDirty option passed to setValue, so
        // disablePacing()'s reset above (current value changes to '' while pacing isn't known to
        // be enabled yet) also shows up as dirty. A non-empty current value is what actually tells
        // a real user edit apart from that reset.
        const hasUserEnteredBudget = !!dirtyFields.monetizing_budget && !!getValues(dealFormField.MONETIZING_BUDGET);
        const hasUserEnteredCadenceUnit =
          !!dirtyFields.cadence_type && !!getValues(dealFormField.MONETIZING_CADENCE_UNIT);

        if (!hasCadences && budget && !hasUserEnteredBudget) {
          setValue(dealFormField.MONETIZING_BUDGET, budget.toString(), revalidate);
        }

        if (hasCadences && data.monetizing?.['cadences']?.[0]?.type) {
          if (!hasUserEnteredBudget) {
            setValue(dealFormField.MONETIZING_BUDGET, data.monetizing?.['cadences']?.[0].budget.toString(), revalidate);
          }
          if (!hasUserEnteredCadenceUnit) {
            setValue(dealFormField.MONETIZING_CADENCE_UNIT, data.monetizing?.['cadences']?.[0].type, revalidate);
          }
        }
      }
    }
  }, [
    isAsTemplate,
    data.monetizing,
    data?.observedIsPacingEnabled,
    setValue,
    trigger,
    data?.observedDealType,
    data?.isLoading,
  ]);

  return { isLoading: data?.isLoading };
};
